import { inject, Injectable } from "@angular/core";
import { AccountMarketplace, CampaignType } from "@front/m19-api-client";
import { BrandingFilter } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  CatalogBrandService,
  DspStrategyService,
  ProductGroupService,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
  StrategyCache,
} from "@front/m19-services";
import { TranslocoService } from "@jsverse/transloco";
import { AdType } from "@m19-board/shared/compact-filter/compact-filter.component";
import { BehaviorSubject, combineLatest, filter, map, Observable, startWith, switchMap, take } from "rxjs";
import { Filter, FilterCategory } from "../compact-filter/compact-filter.component";
import { CompactFilterService } from "../compact-filter/compact-filter.service";

export type AsinFilter =
  | FilterCategory.ProductGroup
  | FilterCategory.BrandProducts
  | FilterCategory.ProductTag1
  | FilterCategory.ProductTag2
  | FilterCategory.Strategy
  | FilterCategory.AdType;

@Injectable({ providedIn: "root" })
export class AsinFilterService {
  private accountSelection = inject(AccountSelectionService);
  private translocoService = inject(TranslocoService);
  private productGroupService = inject(ProductGroupService);
  private strategyCache = inject(StrategyCache);
  private asinService = inject(AsinService);
  private catalogBrandService = inject(CatalogBrandService);
  private spStrategyService = inject(SpStrategyService);
  private sbStrategyService = inject(SbStrategyService);
  private sdStrategyService = inject(SdStrategyService);
  private dspStrategiesService = inject(DspStrategyService);
  private compactFilterService = inject(CompactFilterService);

  public productGroups$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.productGroupService.getProductGroups(am.accountId, am.marketplace)),
  );

  public strategies$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
    map((strategies) => Array.from(strategies.values()).filter((s) => !s.isTacos() || s.isSpStrategy())),
  );

  public catalog$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.asinService.getCatalog(am.accountId, am.marketplace)),
  );

  public productTags$ = this.catalog$.pipe(
    map((catalog) => {
      const productTag1Values = new Set<string>();
      const productTag2Values = new Set<string>();
      for (const product of catalog.products) {
        if (product.productTag1) productTag1Values.add(product.productTag1);
        if (product.productTag2) productTag2Values.add(product.productTag2);
      }
      const productTag1Options = Array.from(productTag1Values);
      const productTag2Options = Array.from(productTag2Values);

      return { productTag1Options, productTag2Options };
    }),
  );

  public productTag1$: Observable<string[]> = this.productTags$.pipe(map((tags) => tags.productTag1Options));
  public productTag2$: Observable<string[]> = this.productTags$.pipe(map((tags) => tags.productTag2Options));

  public brandTrafficOptions$: Observable<BrandingFilter[]> =
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am: AccountMarketplace) => this.catalogBrandService.getBrandingFilters(am.accountId, am.marketplace)),
      map((brandingFilters) => brandingFilters),
    );

  private inventoryConfig$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.asinService.getInventoryConfig(am.accountId, am.marketplace)),
  );

  private asinsByStrategy$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) =>
      combineLatest([
        this.spStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
        this.sbStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
        this.sdStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
        this.dspStrategiesService.getStrategiesPerAsin(am.accountId, am.marketplace),
      ]),
    ),
    map(([spAsinStratIndex, sbAsinStratIndex, sdAsinStratIndex, dspAsinStratIndex]) => {
      const asinsByStrategy: Map<number, string[]> = new Map();
      for (const [asin, strats] of [
        ...spAsinStratIndex,
        ...sbAsinStratIndex,
        ...sdAsinStratIndex,
        ...dspAsinStratIndex,
      ]) {
        for (const s of strats) {
          if (!asinsByStrategy.has(s.strategyId!)) {
            asinsByStrategy.set(s.strategyId!, []);
          }
          asinsByStrategy.get(s.strategyId!)!.push(asin);
        }
      }
      return asinsByStrategy;
    }),
  );

  private productTagMaps$ = this.catalog$.pipe(
    map((catalog) => {
      const productTag1Map = new Map<string, string[]>();
      const productTag2Map = new Map<string, string[]>();

      for (const product of catalog.products) {
        if (product.productTag1) {
          if (!productTag1Map.has(product.productTag1)) {
            productTag1Map.set(product.productTag1, []);
          }
          productTag1Map.get(product.productTag1)!.push(product.asin!);
        }

        if (product.productTag2) {
          if (!productTag2Map.has(product.productTag2)) {
            productTag2Map.set(product.productTag2, []);
          }
          productTag2Map.get(product.productTag2)!.push(product.asin!);
        }
      }

      return { productTag1Map, productTag2Map };
    }),
  );

  private selectedFiltersSubject = new BehaviorSubject<Filter<AsinFilter>[]>([]);

  filters$ = combineLatest([
    this.productGroups$,
    this.strategies$,
    this.brandTrafficOptions$,
    this.productTag1$,
    this.productTag2$,
    this.inventoryConfig$,
  ]).pipe(
    map(([productGroups, strategies, brandTrafficOptions, productTag1Values, productTag2Values, inventoryConfig]) => {
      const filters: Filter<AsinFilter>[] = [
        this.compactFilterService.getFilterWithOptions(FilterCategory.ProductGroup, productGroups, {
          searchable: true,
          unique: true,
        }),
        this.compactFilterService.getFilterWithOptions(FilterCategory.Strategy, strategies, {
          searchable: true,
          unique: true,
        }),
        this.compactFilterService.getFilterWithOptions(FilterCategory.BrandProducts, brandTrafficOptions, {
          searchable: true,
          unique: true,
        }),
        this.compactFilterService.getFilterWithOptions(FilterCategory.ProductTag1, productTag1Values, {
          label:
            inventoryConfig.customField1Name ?? this.translocoService.translate("custom-field-edition.custom_field_1"),
          searchable: true,
          unique: true,
        }),
        this.compactFilterService.getFilterWithOptions(FilterCategory.ProductTag2, productTag2Values, {
          label:
            inventoryConfig.customField2Name ?? this.translocoService.translate("custom-field-edition.custom_field_2"),
          searchable: true,
          unique: true,
        }),
      ];
      return filters;
    }),
    startWith([]),
  );

  filteredAsins$ = combineLatest([
    this.selectedFiltersSubject.asObservable(),
    this.asinsByStrategy$,
    this.productTagMaps$,
  ]).pipe(
    map(([filters, stratAsinMap, productTags]) => {
      return this.getFilteredAsins(filters, stratAsinMap, productTags.productTag1Map, productTags.productTag2Map);
    }),
    startWith(null),
  );

  selectFilters(filters: Filter<AsinFilter>[]): void {
    this.selectedFiltersSubject.next(filters);
  }

  public getAsinFilters(withAdType = false): Observable<Filter<AsinFilter>[]> {
    return this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        this.filters$.pipe(
          filter((filters) => filters.length > 0),
          take(1),
          map((filters) => {
            if (withAdType && am.dspAdvertiserId !== null) {
              return [
                ...filters,
                this.compactFilterService.getFilterWithOptions<FilterCategory.AdType>(
                  FilterCategory.AdType,
                  [AdType.SponsoredAds, AdType.DSP],
                  { localStorageKey: "adType_filters" },
                ),
              ];
            }
            return filters;
          }),
        ),
      ),
    );
  }

  static asinMatchesFilter(asin: string, filter: Set<string> | null): boolean {
    if (!filter) return true;
    return filter.has(asin);
  }

  private getFilteredAsins(
    filters: Filter<FilterCategory>[],
    stratAsinMap: Map<number, string[]>,
    productTag1Map: Map<string, string[]>,
    productTag2Map: Map<string, string[]>,
  ): Set<string> | null {
    if (filters.length === 0 || filters.every((f) => f.values?.length === 0 || f.type === FilterCategory.AdType)) {
      return null;
    }
    let asins: Set<string> = new Set();
    for (const filter of filters) {
      if (filter.values?.length === 0) continue;

      if (filter.type === FilterCategory.ProductGroup) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductGroup);
        const productGroupAsins = new Set(f?.values?.flatMap((pg) => pg.items) ?? []);
        asins = !asins.size ? productGroupAsins : new Set([...asins].filter((x) => productGroupAsins.has(x)));
      } else if (filter.type === FilterCategory.Strategy) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.Strategy);
        const strategyAsins = new Set(f?.values?.flatMap((s) => stratAsinMap.get(s.strategyId!)!) ?? []);
        asins = !asins.size ? strategyAsins : new Set([...asins].filter((x) => strategyAsins.has(x)));
      } else if (filter.type === FilterCategory.BrandProducts) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.BrandProducts);
        const brandAsins = new Set(f?.values?.flatMap((b) => b.asins) ?? []);
        asins = !asins.size ? brandAsins : new Set([...asins].filter((x) => brandAsins.has(x)));
      } else if (filter.type === FilterCategory.ProductTag1) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductTag1);
        const asinsWithProductTag = new Set(f?.values?.flatMap((v) => productTag1Map.get(v)!) ?? []);
        asins = !asins.size ? asinsWithProductTag : new Set([...asins].filter((x) => asinsWithProductTag.has(x)));
      } else if (filter.type === FilterCategory.ProductTag2) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductTag2);
        const asinsWithProductTag = new Set(f?.values?.flatMap((v) => productTag2Map.get(v)!) ?? []);
        asins = !asins.size ? asinsWithProductTag : new Set([...asins].filter((x) => asinsWithProductTag.has(x)));
      }
    }
    return asins;
  }

  private getAdTypeFromCampaignTypes(campaignTypes: CampaignType): AdType {
    switch (campaignTypes) {
      case CampaignType.DSP:
        return AdType.DSP;
      default:
        return AdType.SponsoredAds;
    }
  }
}
